/** Ligne de démarche réutilisée par le tableau de bord et la liste. */

import { Link } from "react-router-dom";

import type { TaskListItem } from "../api/types";
import {
  formatAmount,
  formatAwaitedDistance,
  formatDeadlineDistance,
  formatShortDate,
} from "../lib/format";
import {
  DeadlineBadge,
  ObligationBadge,
  PriorityBadge,
  StatusBadge,
} from "./ui";

export function TaskRow({
  task,
  showWhy = false,
  showStatus = true,
}: {
  task: TaskListItem;
  showWhy?: boolean;
  showStatus?: boolean;
}) {
  const priorityClass =
    task.priority_level === "urgent"
      ? "task-item--urgent"
      : task.priority_level === "high"
        ? "task-item--high"
        : task.priority_level === "anticipate"
          ? "task-item--anticipate"
          : "";

  return (
    <li>
      <Link to={`/demarches/${task.id}`} className={`task-item ${priorityClass}`}>
        <div className="task-item__top">
          <span className="task-item__title">{task.title}</span>
          <PriorityBadge
            level={task.priority_level}
            label={task.priority_level_label}
            score={task.priority_score}
          />
          {showStatus && (
            <StatusBadge status={task.status} label={task.status_label} />
          )}
        </div>

        <div className="task-item__meta">
          <span>
            {/* Un événement attendu ne porte pas une échéance mais la fin d'un
                délai annoncé par un organisme : le dire évite de laisser croire
                à un retard de l'utilisateur. */}
            {task.kind === "expected_event" ? (
              task.due_on ? (
                <>
                  Délai indicatif jusqu'au {formatShortDate(task.due_on)} —{" "}
                  {formatAwaitedDistance(task.days_until_due)}
                </>
              ) : (
                "Aucun délai annoncé"
              )
            ) : task.due_on ? (
              <>
                Échéance {formatShortDate(task.due_on)} —{" "}
                {formatDeadlineDistance(task.days_until_due)}
              </>
            ) : (
              "Sans échéance connue"
            )}
          </span>
          {task.authority && <span>· {task.authority}</span>}
          {task.kind === "expected_event" ? (
            <span className="badge badge--event">{task.kind_label}</span>
          ) : (
            <ObligationBadge
              obligation={task.obligation}
              label={task.obligation_label}
            />
          )}
          {task.indicative_delay_elapsed && (
            <span className="badge badge--warning">Toujours rien reçu ?</span>
          )}
          {task.kind === "expected_event" &&
            task.event_outcome &&
            task.event_outcome !== "awaiting" && (
              <span className="badge">{task.event_outcome_label}</span>
            )}
          {task.kind !== "expected_event" &&
            task.deadline_confidence !== "official" &&
            task.deadline_confidence !== "user_defined" &&
            task.due_on && (
              <DeadlineBadge
                confidence={task.deadline_confidence}
                label={task.deadline_confidence_label}
              />
            )}
          {task.verification_status === "needs_verification" && (
            <span className="badge badge--warning">Donnée à vérifier</span>
          )}
          {task.has_missing_information && (
            <span className="badge badge--warning">Information manquante</span>
          )}
          {task.source === "manual" && <span className="badge">Démarche personnelle</span>}
          {task.documents_count > 0 && (
            <span className="badge">
              {task.documents_count} justificatif
              {task.documents_count > 1 ? "s" : ""}
            </span>
          )}
          {task.amount_kind === "known" && task.amount_cents !== null && (
            <span className="badge badge--info">
              {formatAmount(task.amount_cents, task.amount_currency)}
            </span>
          )}
          {task.amount_kind === "estimated" && (
            <span className="badge">Montant estimé</span>
          )}
        </div>

        {showWhy && task.priority_explanation && (
          <p className="task-item__why mb-0">{task.priority_explanation}</p>
        )}
      </Link>
    </li>
  );
}

export function TaskList({
  tasks,
  showWhy = false,
  showStatus = true,
}: {
  tasks: TaskListItem[];
  showWhy?: boolean;
  showStatus?: boolean;
}) {
  return (
    <ul className="task-list">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          showWhy={showWhy}
          showStatus={showStatus}
        />
      ))}
    </ul>
  );
}
